import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'clsx';
import styles from './index.module.scss';
// 组件
import CommonButton from '@/app/csr-src/pages/order/components/commonButton';
import ZECustomerService from '@/components/customerService';
import { IntlPopover } from '@/components/intlMui';
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import { Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
// 接口
import { fetchCalendarList } from '@/apis/afterSales/afterSales.api';
import { CalendarData, ICalendarListReq } from '@/apis/afterSales/afterSales.interface';
import to from 'await-to-js';
// 图标
import Expand from '@/assets/icons/chevron-down.svg';
import SelectedIcon from '@/assets/icons/SelectedIcon.svg';
import UKToast from '@/components/UKToast';

interface dateSelectProps {
  // selectValue: string;
  // selectList: AddressVo[] | [];
  // placeholder?: string;
  // addAddressClick: () => void;
  // defaultAddress?: ReturnCustomerInfoVo | null;
  requestData: ICalendarListReq;
  position?: string;
  selectedInfo?: any;
  selectChangeHandler?: (value: any) => void;
  cancelRequest?: () => void;
  defaultList?: CalendarData[] | [];
}

const dateSelect: React.FC<dateSelectProps> = observer(
  ({
    requestData,
    selectChangeHandler,
    position = 'top',
    selectedInfo,
    cancelRequest,
    defaultList,
  }) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null); // 弹窗元素
    const [popoverWidth, setPopoverWidth] = useState('');

    const [curDateIndex, setCurDateIndex] = useState(-1);
    const [curTimeIndex, setCurTimeIndex] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(false);
    const [calendarList, setCalendarList] = useState<CalendarData[] | []>([]);
    const [errorTip, setErrorTip] = useState<boolean>(false);

    const reTryTimes = useRef(0);
    const zeRef = useRef<any>(null);

    // if (!selectList?.length) {
    //   return null;
    // }
    useEffect(() => {
      if (defaultList) {
        if (defaultList?.length) {
          setCalendarList(defaultList as CalendarData[]);
          // 从详情页进入，修改预约时间
          if (selectedInfo) {
            // 设置默认选中的时间段
            const parentIndex = defaultList.findIndex(
              (date) => date.promiseDisplayDate === selectedInfo.date,
            );
            const childIndex = defaultList[parentIndex]?.timeList?.findIndex(
              (time) => time.timeRange === selectedInfo.time,
            );

            if (parentIndex !== -1 && childIndex !== -1) {
              setCurDateIndex(parentIndex);
              setCurTimeIndex(`${parentIndex}-${childIndex}`);
              // 解决用户不在弹框里选择，直接保存的场景
              selectChangeHandler &&
                selectChangeHandler({
                  ...defaultList[parentIndex],
                  ...defaultList[parentIndex]?.timeList[childIndex],
                });
            } else {
              // 售后服务单改约的时候，如果之前选择的日期在改约的时候不可选，日期、时间段都不默认勾选
              setCurDateIndex(-1);
              setCurTimeIndex('');
            }
          }
        } else {
          // 接口返回失败的时候，列表为空，不展示任何时间段
          setCalendarList([]);
          setCurDateIndex(-1);
          setCurTimeIndex('');
        }
      } else {
        loadCalendarList();
      }
    }, [requestData.pickwareAddrFourthId]);

    // 获取驿站信息列表
    const loadCalendarList = useCallback(async () => {
      setLoading(true);
      const [err, calendarDataRes] = await to(fetchCalendarList(requestData));

      if (err) {
        // setError("Failed to fetch address list");
        setCalendarList([]);
      } else {
        if (calendarDataRes?.length) {
          setCalendarList(calendarDataRes as CalendarData[]);
          // 从详情页进入，修改预约时间
          if (selectedInfo) {
            // 设置默认选中的时间段
            const parentIndex = calendarDataRes.findIndex(
              (date) => date.promiseDisplayDate === selectedInfo.date,
            );
            const childIndex = calendarDataRes[parentIndex]?.timeList?.findIndex(
              (time) => time.timeRange === selectedInfo.time,
            );

            if (parentIndex !== -1 && childIndex !== -1) {
              setCurDateIndex(parentIndex);
              setCurTimeIndex(`${parentIndex}-${childIndex}`);
              // 解决用户不在弹框里选择，直接保存的场景
              selectChangeHandler &&
                selectChangeHandler({
                  ...calendarDataRes[parentIndex],
                  ...calendarDataRes[parentIndex]?.timeList[childIndex],
                });
            } else {
              // 售后服务单改约的时候，如果之前选择的日期在改约的时候不可选，日期、时间段都不默认勾选
              setCurDateIndex(-1);
              setCurTimeIndex('');
            }
          } else {
            // 从申请页进入，选择预约时间
            // 设置默认选中的时间段
            const parentIndex = calendarDataRes.findIndex((date) =>
              date.timeList.some((time: any) => time.selected === true),
            );
            const childIndex = calendarDataRes[parentIndex]?.timeList?.findIndex(
              (time: any) => time.selected === true,
            );
            if (parentIndex !== -1) {
              setCurDateIndex(parentIndex);
              if (childIndex !== -1) {
                setCurTimeIndex(`${parentIndex}-${childIndex}`);
                // 解决用户不在弹框里选择，直接保存的场景
                selectChangeHandler &&
                  selectChangeHandler({
                    ...calendarDataRes[parentIndex],
                    ...calendarDataRes[parentIndex]?.timeList[childIndex],
                  });
              }
            } else {
              setCurDateIndex(-1);
              setCurTimeIndex('');
            }
          }
        } else {
          // 接口返回失败的时候，列表为空，不展示任何时间段
          setCalendarList([]);
          setCurDateIndex(-1);
          setCurTimeIndex('');
        }
        // setError(null);
      }
      setLoading(false);
    }, [requestData.pickwareAddrFirstId]);

    const handlePopoverClose = () => {
      // 先固定宽度，再触发关闭
      setPopoverWidth(`${anchorEl?.clientWidth}px`);
      setAnchorEl(null);
    };

    const popoverOpen = Boolean(anchorEl);
    const id = popoverOpen ? 'simple-popover' : undefined;
    const curTimeList = calendarList[curDateIndex]?.timeList || [];
    const showDateText =
      (curTimeIndex && calendarList[curTimeIndex.split('-')?.[0] as any]?.promiseDisplayDate) ||
      selectedInfo?.date;
    const showTimeText =
      (curTimeIndex && curTimeList[curTimeIndex.split('-')?.[1] as any]?.timeRange) ||
      selectedInfo?.time;

    return (
      <div className={styles.selectWrap}>
        <div
          className={cn(styles.selectShowWrap, {
            [styles.selectShowLoadingWrap]: loading && !selectedInfo,
          })}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            if (!loading) {
              if (calendarList?.length) {
                if (curTimeIndex !== '') {
                  setCurDateIndex(Number(curTimeIndex.split('-')?.[0]));
                } else {
                  // 售后服务单改约的时候，如果之前选择的日期在改约的时候不可选，日期、时间段都不默认勾选
                  setCurDateIndex(-1);
                }
                setAnchorEl(event.currentTarget);
              } else {
                // 没有数据，重新加载接口两次，最终弹框提示
                reTryTimes.current += 1;
                if (reTryTimes.current > 2) {
                  setErrorTip(true);
                } else {
                  UKToast.show({
                    content: t('order.orderDetail.dateSelect.errorToast'), // 弹窗内容
                    icon: 'warning', // 可选：success, fail, loading, warn, info，影响图标
                  });
                  loadCalendarList();
                }
              }
            }
          }}
        >
          {loading && !selectedInfo ? (
            <>
              <UKLoadingIcon className={styles.loadingIcon} type={CIRCLE_COLOR_MAP.BLACK} />{' '}
              {t('productPool.loading')}
            </>
          ) : (
            <>
              <div>
                {showDateText}
                {showTimeText ? `, ${showTimeText}` : ''}
              </div>
              <Expand className={cn(styles.expandStyle, { [styles.retractStyle]: popoverOpen })} />
            </>
          )}
        </div>
        <IntlPopover
          id={id}
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          sx={{
            // 关键：设置 Popover 内容区域宽度
            '& .MuiPopover-paper': {
              width: popoverWidth || `${anchorEl?.clientWidth}px`,
            },
          }}
          anchorOrigin={{
            vertical: position,
          }}
          transformOrigin={{
            vertical: position === 'bottom' ? 'top' : 'bottom',
          }}
        >
          <div className={styles.dateListWrap}>
            <div className={styles.dateLeft}>
              {calendarList.map((item, index) => {
                return (
                  <div
                    className={cn(styles.dateItem, {
                      [styles.dateActiveItem]: index === curDateIndex,
                    })}
                    key={`pick-up-date-${index}`}
                    onClick={() => {
                      setCurDateIndex(index);
                    }}
                  >
                    {item?.promiseDisplayDate}
                  </div>
                );
              })}
            </div>
            <div className={styles.timeRight}>
              {curTimeList?.length ? (
                curTimeList?.map((ele, inde) => {
                  return (
                    <div
                      className={cn(styles.timeWrap, {
                        [styles.timeActiveItem]: curTimeIndex === `${curDateIndex}-${inde}`,
                      })}
                      key={`pick-up-time-${inde}`}
                      onClick={() => {
                        setCurTimeIndex(`${curDateIndex}-${inde}`);
                        selectChangeHandler &&
                          selectChangeHandler({
                            ...calendarList[curDateIndex],
                            ...curTimeList[inde],
                          });
                        handlePopoverClose();
                      }}
                    >
                      <div>{ele?.timeRange}</div>
                      {curTimeIndex === `${curDateIndex}-${inde}` && (
                        <SelectedIcon className={styles.selectedStyle} />
                      )}
                    </div>
                  );
                })
              ) : curDateIndex !== -1 ? (
                <div
                  className={cn(styles.timeWrap, {
                    [styles.timeActiveItem]: curTimeIndex === `${curDateIndex}-unknown`,
                  })}
                  onClick={() => {
                    setCurTimeIndex(`${curDateIndex}-unknown`);
                    selectChangeHandler &&
                      selectChangeHandler({
                        ...calendarList[curDateIndex],
                      });
                    handlePopoverClose();
                  }}
                >
                  {t('order.orderDetail.dateSelect.noItems')}
                  {curTimeIndex === `${curDateIndex}-unknown` && (
                    <SelectedIcon className={styles.selectedStyle} />
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </IntlPopover>
        {/* 日历接口异常提示弹框 */}
        <Dialog
          open={errorTip}
          onClose={() => setErrorTip(false)}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogContent>
            <DialogContentText>{t('order.orderDetail.dateSelect.tipText')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <CommonButton
              variant='outlined'
              onClick={() => {
                setErrorTip(false);
                cancelRequest && cancelRequest();
              }}
            >
              {t('order.orderDetail.dateSelect.cancelBtn')}
            </CommonButton>
            <CommonButton
              variant='contained'
              sx={{ boxShadow: 'none' }}
              onClick={() => {
                setErrorTip(false);
                if(zeRef.current) {
                  zeRef.current.openService()
                }
              }}
            >
              {t('order.orderDetail.dateSelect.contactBtn')}
            </CommonButton>
          </DialogActions>
        </Dialog>
         {/* 客服组件引入 */}
        <ZECustomerService ref={zeRef} />
      </div>
    );
  },
);

export default dateSelect;
